import axios from 'src/utils/axios';

const BASE = 'v2/user/elevated-auth';

export interface ElevatedAuthStatusResponse {
  verified: boolean;
  expires_at: string | null;
  totp_enabled: boolean;
  method: 'otp' | 'totp' | null;
}

export interface ElevatedAuthRequestResponse {
  dispatch_id: string | null;
  channel: string;
  masked_target: string;
  expires_at: string | null;
}

export interface ElevatedAuthDispatchCooldownError {
  error: string;
  error_code: 'dispatch_cooldown';
  cooldown_seconds_remaining: number;
}

export interface ElevatedAuthVerifyResponse {
  verified_at: string;
  expires_at: string;
  method: 'otp' | 'totp';
}

async function getStatus(): Promise<ElevatedAuthStatusResponse> {
  const resp = await axios.get(`${BASE}/status/`);
  return resp.data as ElevatedAuthStatusResponse;
}

async function requestCode(
  channel: 'email' | 'sms' | 'totp'
): Promise<ElevatedAuthRequestResponse> {
  const resp = await axios.post(`${BASE}/request/`, { channel });
  return resp.data as ElevatedAuthRequestResponse;
}

async function verifyCode(code: string): Promise<ElevatedAuthVerifyResponse> {
  const resp = await axios.post(`${BASE}/verify/`, { code });
  return resp.data as ElevatedAuthVerifyResponse;
}

/**
 * ENG-2745: Verify a backup / recovery code via the dedicated endpoint.
 * Recovery codes are single-use 10-character alphanumeric strings and are
 * NOT handled by the standard elevated-auth verify endpoint.
 *
 * Handles two backend response shapes:
 *  - New (ENG-2745 merged): { verified_at, expires_at, method: 'totp' }
 *  - Legacy (pre-ENG-2745): { confirmed: true | false }
 *
 * Normalises both so callers always receive ElevatedAuthVerifyResponse on
 * success and a rejected promise on failure, matching verifyCode() behaviour.
 */
async function verifyRecoveryCode(
  code: string
): Promise<ElevatedAuthVerifyResponse> {
  const resp = await axios.post('v2/user/totp/recovery-codes/use/', { code });
  const data = resp.data as {
    verified_at?: string;
    expires_at?: string;
    method?: string;
    confirmed?: boolean;
  };

  // Explicit failure signal — both old and new backend use this shape.
  if (data.confirmed === false) {
    throw Object.assign(new Error('Invalid or already used recovery code.'), {
      response: { status: 400 },
    });
  }

  // New backend format (ENG-2745): full elevated-auth response included.
  if (data.expires_at) {
    return data as ElevatedAuthVerifyResponse;
  }

  // Legacy backend format: { confirmed: true } — backend granted elevated auth
  // but didn't return expires_at. Re-fetch status to get the current session.
  if (data.confirmed === true) {
    const freshStatus = await getStatus();
    if (freshStatus.verified && freshStatus.expires_at) {
      return {
        verified_at: new Date().toISOString(),
        expires_at: freshStatus.expires_at,
        method: 'totp',
      };
    }
  }

  // Truly unexpected response shape.
  throw Object.assign(
    new Error('Unexpected response from recovery code endpoint.'),
    { response: { status: 400 } }
  );
}

async function refresh(): Promise<{ expires_at: string }> {
  const resp = await axios.post(`${BASE}/refresh/`);
  return resp.data as { expires_at: string };
}

const elevatedAuthService = {
  getStatus,
  requestCode,
  verifyCode,
  verifyRecoveryCode,
  refresh,
};
export default elevatedAuthService;
